import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * RequireAuth route guard.
 *
 * Wraps authenticated route subtrees. Behavior:
 *   - While AuthContext is hydrating the user from storage → render a
 *     minimal full-page spinner so we don't flash the login screen at
 *     signed-in users on refresh.
 *   - Unauthenticated → bounce to /login, preserving the original target
 *     in `location.state.from` so the login screen can send the user back
 *     after sign-in.
 *   - Authenticated → render the nested routes via <Outlet />.
 */
export function RequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[var(--background)]"
        aria-busy="true"
        aria-live="polite"
      >
        <div
          className="w-8 h-8 rounded-full border-2 border-[var(--brand)] border-t-transparent animate-spin"
          role="status"
          aria-label="Loading your session"
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}
